import { addDays, isIsoDate, weekday as weekdayOf } from "./dates";
import { DomainError } from "./errors";
import { MAX_DOCUMENT_CENTS } from "./quotes";

// ===== Financeiro (T1.9, RF-FIN-01/02/06/07) =====

/** Valor máximo de um título, de uma baixa e de um lançamento (o mesmo teto dos documentos de venda). */
export const MAX_TITLE_CENTS = MAX_DOCUMENT_CENTS;
/** Saldo máximo (em módulo) de uma conta financeira: conferido na posição resultante de toda escrita. */
export const MAX_ACCOUNT_BALANCE_CENTS = 1_000_000_000_000_000;
/** Parcelas de uma conta parcelada e ocorrências de uma recorrente, criadas na inclusão. */
export const MAX_TITLE_OCCURRENCES = 60;
export const DEFAULT_RECURRENCES = 12;
/** Baixas ativas (não estornadas) por título. */
export const MAX_SETTLEMENTS_PER_TITLE = 100;
/** "Baixar selecionados": títulos por requisição. */
export const MAX_BULK_SETTLE = 50;
/** Níveis da árvore de categorias (raiz = 1). */
export const MAX_CATEGORY_DEPTH = 3;
/** Tetos de cadastro por empresa (422 `limit_reached`, também na restauração). */
export const MAX_FINANCIAL_ACCOUNTS = 100;
export const MAX_FINANCIAL_CATEGORIES = 1_000;
export const MAX_PAYMENT_METHODS = 100;
/** Títulos em aberto (abertos ou parciais, os dois lados) por empresa: 422 `limit_reached` na inclusão. */
export const MAX_ACTIVE_TITLES = 200_000;
/** Juros ao mês, multa e taxa da forma de pagamento: 0% a 100%. */
export const MAX_FINANCE_PCT4 = 1_000_000;
export const MAX_SETTLEMENT_DAYS = 365;

export const TITLE_SIDES = ["R", "P"] as const;
export type TitleSide = (typeof TITLE_SIDES)[number];
export const TITLE_STATUSES = ["open", "partial", "paid", "canceled"] as const;
export type TitleStatus = (typeof TITLE_STATUSES)[number];
export const OCCURRENCES = [
  "single",
  "installment",
  "monthly",
  "bimonthly",
  "quarterly",
  "semiannual",
  "annual",
  "biweekly",
  "weekly",
] as const;
export type Occurrence = (typeof OCCURRENCES)[number];
export const ACCOUNT_KINDS = ["cash", "bank", "card_acquirer", "wallet"] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];
export const CATEGORY_KINDS = ["revenue", "expense"] as const;
export type CategoryKind = (typeof CATEGORY_KINDS)[number];
export const DRE_GROUPS = [
  "gross_revenue",
  "deductions",
  "cogs",
  "opex",
  "financial",
  "taxes",
  "other",
] as const;
export type DreGroup = (typeof DRE_GROUPS)[number];
export const BANK_TX_KINDS = ["manual", "settlement", "transfer"] as const;
export type BankTxKind = (typeof BANK_TX_KINDS)[number];

/** Meio de pagamento da NF-e (tPag). */
export const SEFAZ_PAYMENT_TYPES = [
  "01",
  "02",
  "03",
  "04",
  "05",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "90",
  "99",
] as const;
export type SefazPaymentType = (typeof SEFAZ_PAYMENT_TYPES)[number];

const DRE_BY_KIND: Readonly<Record<CategoryKind, readonly DreGroup[]>> = {
  revenue: ["gross_revenue", "financial", "other"],
  expense: ["deductions", "cogs", "opex", "financial", "taxes", "other"],
};
/** Grupos de DRE que fazem sentido para o tipo da categoria. */
export const dreGroupsFor = (kind: CategoryKind): readonly DreGroup[] => DRE_BY_KIND[kind];
/** Contas a receber usam categorias de receita; a pagar, de despesa. */
export const categoryKindOf = (side: TitleSide): CategoryKind => (side === "R" ? "revenue" : "expense");

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};
const isInt = (v: number) => Number.isSafeInteger(v);
const cents = (v: number, what: string, max = MAX_TITLE_CENTS) => {
  if (!isInt(v) || v < 0 || v > max) fail("invalid_money", `${what} inválido`);
  return v;
};
const pct = (v: number, what: string) => {
  if (!isInt(v) || v < 0 || v > MAX_FINANCE_PCT4) fail("invalid_pct", `${what} inválido`);
  return v;
};

const minBig = (a: bigint, b: bigint) => (a < b ? a : b);

/** Divisão half-up de não negativos (`num` ≥ 0, `den` > 0). */
const roundDiv = (num: bigint, den: bigint): bigint => num / den + (2n * (num % den) >= den ? 1n : 0n);

const DAY_MS = 86_400_000;
const msOf = (iso: string) => {
  if (!isIsoDate(iso)) fail("invalid_date", `data inválida: ${iso}`);
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const t = new Date(0);
  t.setUTCFullYear(y, m - 1, d);
  return t.getTime();
};

/** Dias corridos de `a` até `b` (negativo quando `b` é antes). */
export const daysBetween = (a: string, b: string): number => Math.round((msOf(b) - msOf(a)) / DAY_MS);

/**
 * Juros e multa da baixa (05 §7): `dias = max(0, pagamento − vencimento)`; multa = % sobre a base se houver atraso;
 * juros simples pró-rata dia = round(base × juros_mês / 30 × dias). A base é o valor da baixa (o saldo, na baixa total).
 * Conta em BigInt, arredondamento half-up. Teto: base + multa + juros nunca passa de MAX_TITLE_CENTS (a multa é
 * limitada primeiro, depois os juros); sem ele, um atraso longo daria juros acima de 2^53 e a baixa seria recusada.
 */
export function lateCharges(i: {
  baseCents: number;
  dueDate: string;
  paidAt: string;
  interestMonthPct4: number;
  finePct4: number;
}): { daysLate: number; fineCents: number; interestCents: number } {
  const base = BigInt(cents(i.baseCents, "Valor"));
  const interest = BigInt(pct(i.interestMonthPct4, "Juros"));
  const fine = BigInt(pct(i.finePct4, "Multa"));
  const daysLate = Math.max(0, daysBetween(i.dueDate, i.paidAt));
  if (daysLate === 0) return { daysLate, fineCents: 0, interestCents: 0 };
  const room = BigInt(MAX_TITLE_CENTS) - base;
  const fineCents = minBig(roundDiv(base * fine, 1_000_000n), room);
  const interestCents = minBig(roundDiv(base * interest * BigInt(daysLate), 30_000_000n), room - fineCents);
  return { daysLate, fineCents: Number(fineCents), interestCents: Number(interestCents) };
}

/**
 * Valores de uma baixa. `amountCents` é a parte do saldo que a baixa quita; o desconto sai dela, juros e multa somam:
 * pago = valor − desconto + juros + multa. A conta recebe `pago − taxa` (a receber) ou paga `pago + taxa` (a pagar).
 * Pago (e pago + taxa, a pagar) até MAX_TITLE_CENTS, senão `charges_limit`.
 */
export function settlementAmounts(i: {
  side: TitleSide;
  amountCents: number;
  discountCents: number;
  interestCents: number;
  fineCents: number;
  feeCents: number;
}): { paidCents: number; accountDeltaCents: number } {
  const amount = cents(i.amountCents, "Valor da baixa");
  if (amount === 0) fail("invalid_money", "Informe o valor da baixa");
  const discount = cents(i.discountCents, "Desconto");
  const interest = cents(i.interestCents, "Juros");
  const fine = cents(i.fineCents, "Multa");
  const fee = cents(i.feeCents, "Taxa");
  if (discount > amount) fail("invalid_discount", "O desconto não pode passar do valor da baixa");
  const paid = amount - discount + interest + fine;
  if (paid > MAX_TITLE_CENTS || (i.side === "P" && paid + fee > MAX_TITLE_CENTS))
    fail(
      "charges_limit",
      "O valor pago (valor − desconto + juros + multa, mais a taxa na conta a pagar) passaria do limite de uma baixa. Reduza os juros, a multa ou a taxa informados, ou baixe em partes.",
    );
  if (i.side === "R" && fee > paid) fail("invalid_fee", "A taxa não pode passar do valor recebido");
  return { paidCents: paid, accountDeltaCents: i.side === "R" ? paid - fee : -(paid + fee) };
}

/** Situação pelo quanto do valor já foi quitado (cancelado é decidido à parte). */
export const titleStatus = (amountCents: number, paidCents: number): Exclude<TitleStatus, "canceled"> =>
  paidCents <= 0 ? "open" : paidCents >= amountCents ? "paid" : "partial";

export const isOverdue = (status: TitleStatus, dueDate: string, today: string): boolean =>
  (status === "open" || status === "partial") && dueDate < today;

const pad = (n: number, w = 2) => String(n).padStart(w, "0");

/** Soma meses mantendo o dia âncora; mês sem esse dia cai no último dia (dia 31 → 30, 29 ou 28). */
export function addMonthsAnchored(iso: string, months: number, anchorDay: number): string {
  msOf(iso);
  const [y, m] = iso.split("-").map(Number) as [number, number];
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = total - ny * 12 + 1;
  const leap = (ny % 4 === 0 && ny % 100 !== 0) || ny % 400 === 0;
  const lastDay = nm === 2 ? (leap ? 29 : 28) : [4, 6, 9, 11].includes(nm) ? 30 : 31;
  return `${pad(ny, 4)}-${pad(nm)}-${pad(Math.min(anchorDay, lastDay))}`;
}

const MONTH_STEP: Partial<Record<Occurrence, number>> = {
  installment: 1,
  monthly: 1,
  bimonthly: 2,
  quarterly: 3,
  semiannual: 6,
  annual: 12,
};
const DAY_STEP: Partial<Record<Occurrence, number>> = { biweekly: 14, weekly: 7 };

/** Mensagem para a UI ou null: única = 1; parcelada = 2..60; recorrentes = 1..60. */
export function occurrenceProblem(occurrence: Occurrence, count: number): string | null {
  if (occurrence === "single") return count === 1 ? null : "Conta única tem uma ocorrência";
  const min = occurrence === "installment" ? 2 : 1;
  if (!isInt(count) || count < min || count > MAX_TITLE_OCCURRENCES)
    return `Informe de ${min} a ${MAX_TITLE_OCCURRENCES} ${occurrence === "installment" ? "parcelas" : "ocorrências"}`;
  return null;
}

export type TitleOccurrence = { number: number; dueDate: string; amountCents: number };

/**
 * Ocorrências do título (RF-FIN-01, 05 §4). Parcelada: `floor(valor / n)` e o resto na última, vencimentos mensais a
 * partir do primeiro. Recorrentes: o valor inteiro em cada ocorrência, no passo da periodicidade (mês com âncora no
 * dia do primeiro vencimento). Semanal com `weekday` (0 = domingo): o primeiro vencimento vai para esse dia da semana.
 */
export function buildOccurrences(i: {
  occurrence: Occurrence;
  amountCents: number;
  firstDueDate: string;
  count: number;
  weekday?: number | null;
}): TitleOccurrence[] {
  const amount = cents(i.amountCents, "Valor");
  if (amount === 0) fail("invalid_money", "Informe o valor");
  const problem = occurrenceProblem(i.occurrence, i.count);
  if (problem) fail("invalid_occurrence", problem);
  let first = i.firstDueDate;
  msOf(first);
  if (i.occurrence === "weekly" && i.weekday != null) {
    if (!isInt(i.weekday) || i.weekday < 0 || i.weekday > 6)
      fail("invalid_occurrence", "Dia da semana inválido");
    first = addDays(first, (i.weekday - weekdayOf(first) + 7) % 7);
  }
  const n = i.count;
  const split = i.occurrence === "installment";
  const each = split ? Math.floor(amount / n) : amount;
  if (each === 0) fail("invalid_occurrence", "Valor pequeno demais para tantas parcelas");
  const anchor = Number(first.slice(8, 10));
  const months = MONTH_STEP[i.occurrence];
  const days = DAY_STEP[i.occurrence] ?? 0;
  return Array.from({ length: n }, (_, k) => ({
    number: k + 1,
    dueDate: months !== undefined ? addMonthsAnchored(first, k * months, anchor) : addDays(first, k * days),
    amountCents: split && k === n - 1 ? amount - each * (n - 1) : each,
  }));
}

/** Teto do saldo na posição resultante; o lançamento que aproxima o saldo de zero passa (conserta dado antigo). */
export function assertBalanceWithin(before: bigint, after: bigint): void {
  const max = BigInt(MAX_ACCOUNT_BALANCE_CENTS);
  const abs = (v: bigint) => (v < 0n ? -v : v);
  if (abs(after) > max && abs(after) >= abs(before))
    fail("balance_limit", "O saldo da conta passaria do limite permitido.");
}

/** Soma lida do banco (BigInt) como número, ou null se passar de 2^53 (a tela mostra "—"). */
export const safeCentsOrNull = (v: bigint): number | null => {
  const n = Number(v);
  return Number.isSafeInteger(n) ? n : null;
};
